import PropTypes from "prop-types";
import LocalRanking_File from "../../data/LocalRanking.json";
import ELO_File from "../../data/ELO.json";
import RankPrediction from "../../data/RankPrediction.json";
import styles from "./ELO_Ranking.module.css";
import down_icon from "../../image/down_icon.png";
import Chart from "../Chart.js";
import { useState, useEffect } from "react";
import classNames from "classnames";
import rankLine from "../../global/RankLine.json";
import { smooth, bgColor, litColor } from "../../global/floatColor";
import { probSorter } from "./tsorter.js";

function abbr(stage, language) {
  switch (stage) {
    case "Regular":
      return language === "kr" ? "Reg. 예측" : "Reg. Pr.";
    case "Groups":
      return language === "kr" ? "Groups 예측" : "Groups Pr.";
    case "Playoffs":
      return language === "kr" ? "PO 예측" : "PO Pr.";
    case "ChampionshipPoint":
		case "SeasonFinal-Seed":
      return language === "kr" ? "CP 예측" : "CP Pr.";
    case "Regional":
		case "Worlds-Seed":
      return language === "kr" ? "WQ 예측" : "WQ Pr.";
		case "SeasonFinal":
      return language === "kr" ? "SF 예측" : "SF Pr.";
    default:
      return stage;
  }
}

function title(stage, language) {
  switch (stage) {
    case "Regular":
      return language === "kr" ? "정규시즌 예측" : "Regular Season Prediction";
    case "Groups":
      return language === "kr" ? "조별리그 예측" : "Group Stage Prediction";
    case "Playoffs":
      return language === "kr" ? "플레이오프 예측" : "PlayOff Prediction";
    case "ChampionshipPoint":
		case "SeasonFinal-Seed":
      return language === "kr"
        ? "챔피언십 포인트 예측"
        : "ChampionShip Point Prediction";
    case "Regional":
		case "Worlds-Seed":
      return language === "kr"
        ? "롤드컵 진출 예측"
        : "Worlds Qualification Prediction";
		case "SeasonFinal":
      return language === "kr" ? "시즌 파이널 예측" : "SeasonFinal Prediction";
    default:
      return stage;
  }
}

function LocalRanking({ league, language }) {
  let n = 1;
  // stage to show
  const [stageN, setStageN] = useState(0);
  const [showDetail, setShowDetail] = useState("TBD");

  useEffect(() => {
    setStageN(0);
  }, [league]);

  useEffect(() => {
    setShowDetail("TBD");
    probSorter("id_4", true);
    probSorter("id_3", true);
    probSorter("id_2", true);
    probSorter("id_1", true);
    probSorter("id_0", true);
    probSorter("rank", true);
    probSorter("group", true);
  }, [league, stageN]);

  const stages = RankPrediction[league];
  if (stages[stageN] === undefined) return;
  const stage = RankPrediction[league][stageN].key;

  const currentRanking = LocalRanking_File[league].find(
    (element) => element.key === stage
  ).value;

  return (
    <table className={styles.table} id="Table">
      <thead className={styles.thead}>
        <tr>
          {currentRanking[0].rank === undefined ? null : (
            <th
              rowSpan="2"
              className={styles.rank}
              onClick={() => {
                setShowDetail("TBD");
                probSorter("rank");
              }}
            >
              {language === "kr" ? "순위" : "Rank"}
            </th>
          )}
          <th rowSpan="2" className={styles.team}>
            {language === "kr" ? "팀명" : "Team"}
          </th>
          {currentRanking[0].group === undefined ? null : (
            <th
              rowSpan="2"
              className={styles.win_lose}
              onClick={() => {
                setShowDetail("TBD");
                probSorter("group");
              }}
            >
              {language === "kr" ? "조" : "Group"}
            </th>
          )}
          {currentRanking[0].point === undefined ? null : (
            <th rowSpan="2" className={styles.win_lose}>
              {language === "kr" ? "포인트" : "point"}
            </th>
          )}
          {currentRanking[0].win === undefined ? null : 
          currentRanking[0].draw === undefined ? (
            <th rowSpan="2" className={styles.win_lose}>
              {language === "kr" ? "승-패" : "W-L"}
            </th>
          ) : (
            <th rowSpan="2" className={styles.win_lose}>
              {language === "kr" ? "승-무-패" : "W-D-L"}
            </th>
          )
          }
          {currentRanking[0].diff === undefined ? null : (
            <th
              rowSpan="2"
              className={styles.diff}
              title={language === "kr" ? "세트 득실" : "game difference"}
            >
              {language === "kr" ? "득실" : "Diff"}
            </th>
          )}
          {currentRanking[0].league !== undefined ? (
            <th
              rowSpan="2"
              className={styles.winPr}
              title={
                language === "kr"
                  ? "소속 리그"
                  : "league"
              }
              onClick={() => {
                setShowDetail("TBD");
                probSorter("league");
              }}
            >
              {language === "kr" ? "리그" : "league"}
            </th>
          ) : (
            <th
              rowSpan="2"
              className={styles.winPr}
              title={
                language === "kr"
                  ? "400점 차이 나면 기대승률이 10배 차이난다."
                  : "if the difference is 400 points, then the expected win rate for the stronger team is 10 times bigger than weeker team."
              }
              onClick={() => {
                setShowDetail("TBD");
                probSorter("elo");
              }}
            >
              {language === "kr" ? "ELO 점수" : "ELO point"}
            </th>
          )}
          <th
            colSpan={`${rankLine[league][stage].cut_line.length}`}
            className={styles.prob}
          >
            {stages.map((stage_line, idx) => {
              return [
                <button
                  key={stage_line.key}
                  className={styles.stage_button}
                  onClick={() => {
                    setStageN(idx);
                  }}
                  style={
                    stageN === idx
                      ? { paddingBottom: "1px", borderBottom: "1px solid" }
                      : null
                  }
                  title={title(stages[idx].key, language)}
                >
                  {abbr(stages[idx].key, language)}
                </button>,
                " ",
              ];
            })}
          </th>
        </tr>
        <tr>
          {rankLine[league][stage].inner[language].map((inner, idx) => (
            <th
              key={`inner_${idx}`}
              className={styles.prob_stage}
              title={rankLine[league][stage].title[language][idx]}
              style={{
                width: `${42 / rankLine[league][stage].cut_line.length}%`,
              }}
              onClick={() => {
                setShowDetail("TBD");
                probSorter(`id_${idx}`);
              }}
            >
              {inner}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {currentRanking.map((line, idx) => {
          const standing = stages[stageN].value[line.team];
          const lineELO = ELO_File[line.league?line.league:league][line.team];
          const section = rankLine[league][stage].cut_line;
          const team = line.team;
          let float = [];

          for (let i = 0; i < section.length; i++) {
            float = [...float, 0];
          }
          if (standing !== undefined)
            for (let i = 0; i < Object.keys(standing).length; i++) {
              for (let j = 0; j < section.length; j++)
                if (
                  Array.isArray(section[j])
                    ? section[j].includes(i + 1)
                    : i < section[j]
                )
                  float[j] += standing[i];
            }

          for (let i = 0; i < section.length; i++) {
            float[i] = smooth(float[i]);
          }

          let key = 0;

          return [
            <tr key={team} team={team}>
              {currentRanking[0].rank === undefined ? null : (
                <td
                  className={classNames([styles.block, styles.rank])}
                  id="rank"
                >
                  {line.rank}
                </td>
              )}
              <td className={styles.block}>{line.team}</td>
              {currentRanking[0].group === undefined ? null : (
                <td
                  className={classNames([styles.block, styles.win_lose])}
                  id="group"
                >
                  {line.group}
                </td>
              )}
              {currentRanking[0].point === undefined ? null : (
                <td className={classNames([styles.block, styles.win_lose])}>
                  {line.point}
                </td>
              )}
              {currentRanking[0].win === undefined ? null :
              currentRanking[0].draw === undefined ? (
                <td className={classNames([styles.block, styles.win_lose])}>
                  {line.win}-{line.lose}
                </td>
              ) : (
                <td className={classNames([styles.block, styles.win_lose])}>
                  {line.win}-{line.draw}-{line.lose}
                </td>
              )}
              {currentRanking[0].diff === undefined ? null : (
                <td className={classNames([styles.block, styles.diff])}>
                  {line.diff}
                </td>
              )}
              {/* {currentRanking[0].league === undefined ? null : (
                <td className={classNames([styles.block, styles.web])} id="elo">
                  {lineELO.ELO}
                  {lineELO.ELO_Diff === undefined
                    ? null
                    : [
                        " (",
                        lineELO.ELO_Diff === 0
                          ? "-"
                          : lineELO.ELO_Diff > 0
                          ? "▲" + lineELO.ELO_Diff
                          : "▼" + -lineELO.ELO_Diff,
                        ")",
                      ]}
                </td>
              )} */}
              {currentRanking[0].league !== undefined ? (
                <td
                  className={classNames([
                    styles.block,
                    // styles.mobile,
                    styles.elo,
                  ])}
                  id="league"
                >
                  {line.league}
                </td>
              ) : (
                <td
                  className={classNames([
                    styles.block,
                    // styles.mobile,
                    styles.elo,
                  ])}
                  id="elo"
                >
                  {lineELO.ELO}
                </td>
              )}
              {float.map((f) => (
                <td
                  key={(n, key++)}
                  style={{
                    background: bgColor(f),
                    color: litColor(f),
                  }}
                  className={styles.block}
                  id={`id_${key}`}
                >
                  {`${f}%`}
                </td>
              ))}
              <td className={styles.more}>
                <button
                  className={styles.button}
                  onClick={(event) => {
                    let nextValue = event.nativeEvent
                      .composedPath()[3]
                      .getAttribute("team");
                    if (nextValue === showDetail) setShowDetail("TBD");
                    else setShowDetail(nextValue);
                  }}
                >
                  <img
                    src={down_icon}
                    className={styles.web}
                    alt={language === "kr" ? "더보기" : "Show More"}
                    height="25"
                    width="25"
                    style={
                      team === showDetail ? { transform: "scaleY(-1)" } : null
                    }
                  />
                  <img
                    src={down_icon}
                    className={styles.mobile}
                    alt={language === "kr" ? "더보기" : "Show More"}
                    height="15"
                    width="15"
                    style={
                      team === showDetail ? { transform: "scaleY(-1)" } : null
                    }
                  />
                </button>
              </td>
            </tr>,
            <tr key={`detail_${team}`} team={team}>
              <td
                colSpan={6 + section}
                width="100%"
                className={showDetail === team ? null : styles.unshown}
              >
                <Chart
                  league={league}
                  stage={stage}
                  team={team}
                  language={language}
                />
              </td>
            </tr>,
          ];
        })}
      </tbody>
    </table>
  );
}

LocalRanking.propTypes = {
  league: PropTypes.string.isRequired,
  language: PropTypes.string,
};

export default LocalRanking;
